import { gql } from "@apollo/client";

export const GET_USERS = gql`
  query Users {
    users {
      id
      name
      function
      organisation {
        id
        name
        address
        city
        sector
        organisationType
        subscriptions {
          id
          program {
            id
            name
            image
            edition
            description
            startDate
            endDate
            numberOfPlaces
            state
          }
          user {
            id
            name
            function
            phoneNumber
            emailAddress
            role
            locale
          }
          state
          questionnairreId
        }
      }
      phoneNumber
      emailAddress
      role
      locale
      programs {
        id
        name
        image
        edition
        description
        startDate
        endDate
        numberOfPlaces
        state
        activities {
          id
          title
          description
          activityType
          location {
            id
            street
            houseNumber
            zipCode
            city
            countryCode
            latitude
            longitude
          }
          microsoftTeamsLink
          date
          startTime
          endTime
        }
        mentors {
          id
          name
          function
          phoneNumber
          emailAddress
          role
          locale
        }
        startups {
          id
          state
          questionnairreId
        }
      }
    }
  }
`;

export const ADD_ADMIN = gql`
  mutation AddAdmin($user: TemporaryNewAdmin) {
    addAdmin(user: $user) {
      id
      name
      function
      phoneNumber
      emailAddress
      role
      locale
    }
  }
`;

export const UPDATE_PASSWORD_WITH_HASH = gql`
  mutation UpdatePasswordWithHash(
    $updatePasswordWithHashInput: UpdatePasswordWithHashInput!
  ) {
    updatePasswordWithHash(
      updatePasswordWithHashInput: $updatePasswordWithHashInput
    ) {
      code
    }
  }
`;

export const FORGOT_PASSWORD = gql`
  mutation ForgotPassword($forgotPasswordInput: ForgotPasswordInput!) {
    forgotPassword(forgotPasswordInput: $forgotPasswordInput) {
      code
    }
  }
`;

export const REQUEST_PASSWORD_CHANGE = gql`
  mutation RequestPasswordChange {
    requestChangePassword {
      url
    }
  }
`;

export const ACCESS_TOKEN_LOGIN = gql`
  query AccessTokenByCredentials(
    $accessTokenByCredentialsInput: AccessTokenByCredentialsInput!
  ) {
    accessTokenByCredentials(
      accessTokenByCredentialsInput: $accessTokenByCredentialsInput
    ) {
      accessToken
      refreshToken
      tokenType
      role
    }
  }
`;

export const ME = gql`
  query Me {
    me {
      id
      name
      function
      phoneNumber
      emailAddress
      role
      locale
      organisation {
        id
        name
        logoUrl
        parent {
          name
        }
        features {
          name
          status
        }
      }
    }
  }
`;

export const ME_PLUS_ORG = gql`
  query MePlusOrg {
    me {
      id
      name
      function
      phoneNumber
      emailAddress
      role
      locale
      organisation {
        id
        address
        city
        id
        name
        organisationType
        sector
        contact {
          id
        }
        assessmentRoutes {
          name
          url
        }
      }
    }
  }
`;

export const PROGRAMS = gql`
  query Programs {
    programs {
      id
      name
      image
      edition
      description
      startDate
      endDate
      numberOfPlaces
      state
      activities {
        id
        title
        description
        activityType
        location {
          id
          street
          houseNumber
          zipCode
          city
          countryCode
          latitude
          longitude
        }
        microsoftTeamsLink
        date
        startTime
        endTime
      }
      mentors {
        id
        name
        function
        organisation {
          id
          name
          address
          city
          sector
          organisationType
        }
        phoneNumber
        emailAddress
        role
        locale
        programs {
          id
          name
          image
          edition
          description
          startDate
          endDate
          numberOfPlaces
          state
        }
      }
      startups {
        id
        program {
          id
          name
          image
          edition
          description
          startDate
          endDate
          numberOfPlaces
          state
        }
        user {
          id
          name
          function
          phoneNumber
          emailAddress
          role
          locale
        }
        state
        questionnairreId
      }
    }
  }
`;

export const UPDATE_TOKEN = gql`
  mutation UpdateAccessTokenWithRefreshToken(
    $refreshTokenInput: RefreshTokenInput
  ) {
    updateAccessTokenWithRefreshToken(refreshTokenInput: $refreshTokenInput) {
      accessToken
      refreshToken
      tokenType
    }
  }
`;

export const UPDATE_USER_DETAILS = gql`
  mutation UpdateUserDetails($userDetails: UpdateUserDetailsInput) {
    updateUserDetails(userDetails: $userDetails) {
      id
      name
      function
      phoneNumber
      emailAddress
      role
      locale
      organisation {
        name
        sector
        city
      }
    }
  }
`;

export const PROGRAM_TEMPLATES = gql`
  query ProgramTemplates {
    programTemplates {
      name
    }
  }
`;

export const CREATE_PROGRAM_FROM_TEMPLATE = gql`
  mutation CreateProgramFromTemplate(
    $createProgramFromTemplateInput: CreateProgramFromTemplateInput!
  ) {
    createProgramFromTemplate(
      createProgramFromTemplateInput: $createProgramFromTemplateInput
    ) {
      id
      name
      image
      edition
      description
      startDate
      endDate
      numberOfPlaces
      state
    }
  }
`;

export const PROGRAM = gql`
  query Program($programId: Int!) {
    program(id: $programId) {
      id
      name
      image
      edition
      description
      startDate
      endDate
      numberOfPlaces
      state
      activities {
        id
        title
        description
        activityType
        location {
          id
          street
          houseNumber
          zipCode
          city
          countryCode
          latitude
          longitude
        }
        microsoftTeamsLink
        date
        startTime
        endTime
      }
      mentors {
        id
        name
        function
        organisation {
          id
          name
          address
          city
          sector
          organisationType
        }
        phoneNumber
        emailAddress
        role
        locale
        programs {
          id
          name
          image
          edition
          description
          startDate
          endDate
          numberOfPlaces
          state
        }
      }
      startups {
        id
        program {
          id
          name
          image
          edition
          description
          startDate
          endDate
          numberOfPlaces
          state
        }
        user {
          id
          name
          function
          phoneNumber
          emailAddress
          role
          locale
        }
        state
        questionnairreId
      }
    }
  }
`;

export const UPDATE_ACTIVITIES = gql`
  mutation UpdateActivities($updateActivitiesInput: UpdateActivitiesInput!) {
    updateActivities(updateActivitiesInput: $updateActivitiesInput) {
      id
      name
      image
      edition
      description
      startDate
      endDate
      numberOfPlaces
      state
      activities {
        id
        title
        description
        activityType
        location {
          id
          street
          houseNumber
          zipCode
          city
          countryCode
          latitude
          longitude
        }
        microsoftTeamsLink
        date
        startTime
        endTime
      }
      mentors {
        id
        name
        function
        organisation {
          id
          name
          address
          city
          sector
          organisationType
        }
        phoneNumber
        emailAddress
        role
        locale
        programs {
          id
          name
          image
          edition
          description
          startDate
          endDate
          numberOfPlaces
          state
        }
      }
      startups {
        id
        program {
          id
          name
          image
          edition
          description
          startDate
          endDate
          numberOfPlaces
          state
        }
        user {
          id
          name
          function
          phoneNumber
          emailAddress
          role
          locale
        }
        state
        questionnairreId
      }
    }
  }
`;

export const PLAN_PROGRAM = gql`
  mutation PlanProgram($planProgramId: Int!) {
    planProgram(id: $planProgramId) {
      id
      name
      image
      edition
      description
      startDate
      endDate
      numberOfPlaces
      state
      activities {
        id
        title
        description
        activityType
        location {
          id
          street
          houseNumber
          zipCode
          city
          countryCode
          latitude
          longitude
        }
        microsoftTeamsLink
        date
        startTime
        endTime
      }
      mentors {
        id
        name
        function
        organisation {
          id
          name
          address
          city
          sector
          organisationType
        }
        phoneNumber
        emailAddress
        role
        locale
        programs {
          id
          name
          image
          edition
          description
          startDate
          endDate
          numberOfPlaces
          state
        }
      }
      startups {
        id
        program {
          id
          name
          image
          edition
          description
          startDate
          endDate
          numberOfPlaces
          state
        }
        user {
          id
          name
          function
          phoneNumber
          emailAddress
          role
          locale
        }
        state
        questionnairreId
      }
    }
  }
`;

export const UPDATE_MENTORS_ON_PROGRAM = gql`
  mutation UpdateMentorsOnProgram(
    $updateMentorsOnProgramInput: UpdateMentorsOnProgramInput!
  ) {
    updateMentorsOnProgram(
      updateMentorsOnProgramInput: $updateMentorsOnProgramInput
    ) {
      id
      name
      image
      edition
      description
      startDate
      endDate
      numberOfPlaces
      state
      activities {
        id
        title
        description
        activityType
        location {
          id
          street
          houseNumber
          zipCode
          city
          countryCode
          latitude
          longitude
        }
        microsoftTeamsLink
        date
        startTime
        endTime
      }
      mentors {
        id
        name
        function
        organisation {
          id
          name
          address
          city
          sector
          organisationType
          subscriptions {
            id
            state
            questionnairreId
          }
          parent {
            id
            name
            address
            city
            sector
            organisationType
          }
        }
        phoneNumber
        emailAddress
        role
        locale
        programs {
          id
          name
          image
          edition
          description
          startDate
          endDate
          numberOfPlaces
          state
        }
      }
      startups {
        id
        program {
          id
          name
          image
          edition
          description
          startDate
          endDate
          numberOfPlaces
          state
        }
        user {
          id
          name
          function
          phoneNumber
          emailAddress
          role
          locale
        }
        state
        questionnairreId
      }
    }
  }
`;

export const UPDATE_STARTUPS_ON_PROGRAM = gql`
  mutation UpdateStartupsOnProgram(
    $updateStartupsOnProgramInput: UpdateStartupsOnProgramInput!
  ) {
    updateStartupsOnProgram(
      updateStartupsOnProgramInput: $updateStartupsOnProgramInput
    ) {
      id
      name
      image
      edition
      description
      startDate
      endDate
      numberOfPlaces
      state
      activities {
        id
        title
        description
        activityType
        location {
          id
          street
          houseNumber
          zipCode
          city
          countryCode
          latitude
          longitude
        }
        microsoftTeamsLink
        date
        startTime
        endTime
      }
      mentors {
        id
        name
        function
        organisation {
          id
          name
          address
          city
          sector
          organisationType
        }
        phoneNumber
        emailAddress
        role
        locale
        programs {
          id
          name
          image
          edition
          description
          startDate
          endDate
          numberOfPlaces
          state
        }
      }
      startups {
        id
        program {
          id
          name
          image
          edition
          description
          startDate
          endDate
          numberOfPlaces
          state
        }
        user {
          id
          name
          function
          phoneNumber
          emailAddress
          role
          locale
        }
        state
        questionnairreId
      }
    }
  }
`;

export const ADD_MENTOR = gql`
  mutation AddMentor($addMentorInput: AddMentorInput!) {
    addMentor(addMentorInput: $addMentorInput) {
      id
      name
      function
      organisation {
        id
        name
        address
        city
        sector
        organisationType
        subscriptions {
          id
          program {
            id
            name
            image
            edition
            description
            startDate
            endDate
            numberOfPlaces
            state
          }
          user {
            id
            name
            function
            phoneNumber
            emailAddress
            role
            locale
          }
          state
          questionnairreId
        }
      }
      phoneNumber
      emailAddress
      role
      locale
      programs {
        id
        name
        image
        edition
        description
        startDate
        endDate
        numberOfPlaces
        state
        activities {
          id
          title
          description
          activityType
          location {
            id
            street
            houseNumber
            zipCode
            city
            countryCode
            latitude
            longitude
          }
          microsoftTeamsLink
          date
          startTime
          endTime
        }
        mentors {
          id
          name
          function
          phoneNumber
          emailAddress
          role
          locale
        }
        startups {
          id
          state
          questionnairreId
        }
      }
    }
  }
`;

export const ADD_STARTUP = gql`
  mutation AddStartup($addStartupInput: AddStartupInput) {
    addStartup(addStartupInput: $addStartupInput) {
      id
      name
      function
      organisation {
        id
        name
        address
        city
        sector
        organisationType
        subscriptions {
          id
          program {
            id
            name
            image
            edition
            description
            startDate
            endDate
            numberOfPlaces
            state
          }
          user {
            id
            name
            function
            phoneNumber
            emailAddress
            role
            locale
          }
          state
          questionnairreId
        }
        parent {
          id
          name
          address
          city
          sector
          organisationType
        }
      }
      phoneNumber
      emailAddress
      role
      locale
      programs {
        id
        name
        image
        edition
        description
        startDate
        endDate
        numberOfPlaces
        state
        activities {
          id
          title
          description
          activityType
          location {
            id
            street
            houseNumber
            zipCode
            city
            countryCode
            latitude
            longitude
          }
          microsoftTeamsLink
          date
          startTime
          endTime
        }
        mentors {
          id
          name
          function
          phoneNumber
          emailAddress
          role
          locale
        }
        startups {
          id
          state
          questionnairreId
        }
      }
    }
  }
`;

export const DELETE_PROGRAM = gql`
  mutation DeleteProgram($deleteProgramId: Int!) {
    deleteProgram(id: $deleteProgramId)
  }
`;

export const INFORM_MENTORS = gql`
  mutation InformMentorsForProgram(
    $message: String!
    $informMentorsForProgramId: Int!
  ) {
    informMentorsForProgram(message: $message, id: $informMentorsForProgramId) {
      id
      name
      image
      edition
      description
      startDate
      endDate
      numberOfPlaces
      state
      activities {
        id
        title
        description
        activityType
        location {
          id
          street
          houseNumber
          zipCode
          city
          countryCode
          latitude
          longitude
        }
        microsoftTeamsLink
        date
        startTime
        endTime
      }
      mentors {
        id
        name
        function
        organisation {
          id
          name
          address
          city
          sector
          organisationType
        }
        phoneNumber
        emailAddress
        role
        locale
        programs {
          id
          name
          image
          edition
          description
          startDate
          endDate
          numberOfPlaces
          state
        }
      }
      startups {
        id
        program {
          id
          name
          image
          edition
          description
          startDate
          endDate
          numberOfPlaces
          state
        }
        user {
          id
          name
          function
          phoneNumber
          emailAddress
          role
          locale
        }
        state
        questionnairreId
      }
    }
  }
`;

export const INFORM_STARTUPS = gql`
  mutation InformStartups($informStartupsId: Int!, $message: String!) {
    informStartups(id: $informStartupsId, message: $message) {
      id
      name
      image
      edition
      description
      startDate
      endDate
      numberOfPlaces
      state
      activities {
        id
        title
        description
        activityType
        location {
          id
          street
          houseNumber
          zipCode
          city
          countryCode
          latitude
          longitude
        }
        microsoftTeamsLink
        date
        startTime
        endTime
      }
      mentors {
        id
        name
        function
        organisation {
          id
          name
          address
          city
          sector
          organisationType
        }
        phoneNumber
        emailAddress
        role
        locale
        programs {
          id
          name
          image
          edition
          description
          startDate
          endDate
          numberOfPlaces
          state
        }
      }
      startups {
        id
        program {
          id
          name
          image
          edition
          description
          startDate
          endDate
          numberOfPlaces
          state
        }
        user {
          id
          name
          function
          phoneNumber
          emailAddress
          role
          locale
        }
        state
        questionnairreId
      }
    }
  }
`;

export const UPDATE_SUBSCRIPTION_INVITE = gql`
  mutation UpdateSubscriptionInvite($inviteInput: SubscriptionInviteInput) {
    updateSubscriptionInvite(inviteInput: $inviteInput) {
      id
      program {
        id
        name
        image
        edition
        description
        startDate
        endDate
        numberOfPlaces
        state
        activities {
          id
          title
          description
          activityType
          location {
            id
            street
            houseNumber
            zipCode
            city
            countryCode
            latitude
            longitude
          }
          microsoftTeamsLink
          date
          startTime
          endTime
        }
        mentors {
          id
          name
          function
          phoneNumber
          emailAddress
          role
          locale
          createdAt
          updatedAt
        }
        startups {
          id
          state
          questionnairreId
        }
      }
      user {
        id
        name
        function
        organisation {
          id
          name
          address
          city
          sector
          organisationType
          subscriptions {
            id
            state
            questionnairreId
          }
          createdAt
          updatedAt
        }
        phoneNumber
        emailAddress
        role
        locale
        programs {
          id
          name
          image
          edition
          description
          startDate
          endDate
          numberOfPlaces
          state
        }
        createdAt
        updatedAt
      }
      state
      questionnairreId
    }
  }
`;

export const MY_STARTUPS = gql`
  query MyStartups {
    myStartups {
      id
      name
      function
      organisation {
        id
        name
        address
        city
        sector
        organisationType
        subscriptions {
          id
          program {
            id
            name
            image
            edition
            description
            startDate
            endDate
            numberOfPlaces
            state
          }
          user {
            id
            name
            function
            phoneNumber
            emailAddress
            role
            locale
          }
          state
          questionnairreId
        }
        parent {
          id
          name
          address
          city
          sector
          organisationType
        }
      }
      phoneNumber
      emailAddress
      role
      locale
      programs {
        id
        name
        image
        edition
        description
        startDate
        endDate
        numberOfPlaces
        state
        activities {
          id
          title
          description
          activityType
          location {
            id
            street
            houseNumber
            zipCode
            city
            countryCode
            latitude
            longitude
          }
          microsoftTeamsLink
          date
          startTime
          endTime
        }
        mentors {
          id
          name
          function
          phoneNumber
          emailAddress
          role
          locale
        }
        startups {
          id
          state
          questionnairreId
        }
      }
    }
  }
`;

export const ASSESSMENTS_MINIMAL = gql`
  query ASSESSMENT_BY_ID($noChildAssessments: Boolean) {
    assessmentsMinimal  (noChildAssessments: $noChildAssessments) {
      venture
      uuid
      completionTime
      id
      pdfUrl
      scanType
      organisation {
        name
      }
    }
  }
`;


export const ASSESSMENT_BY_ID = gql`
  query Assessment($assessmentId: Int!) {
    assessment(id: $assessmentId) {
      uuid  
      id
      venture
      founder
      founderEmailAddress
      completionTime
      pdfUrl
      questionnairePdfUrl
      reviewedPdfUrl
      reviewedQuestionnairePdfUrl
      organisation {
        contact {
          emailAddress
        }
      }
    }
  }
`

export const ASSESSMENTS = gql`
    query Assessments {
        assessments {
            completionTime
            createdAt
            id
            link
            venture
            founder
            sector
            contact
            institute {
              name
            }
            organisation {
              organisationType
              name
              sector
              institutionUuid
              logoUrl
              foundingDate
              contact {
                name
                function
              }
              contactPerson
              website
              parent {
                name
              }
            }
            pdfUrl
            questionnairePdfUrl
            reviewedPdfUrl
            reviewedQuestionnairePdfUrl
            status
            uuid
            tags {
              id
              name
            }
        }
    }
`;

export const ASSESSMENTS_INVITES = gql`
  query AssessmentInvites {
    assessmentInvites {
      founder
      uuid
      id
      venture
      emailAddressFounder
      lastReminderSent
      assessment {
        status
      }
      createdAt
      invitee {
        emailAddress
        name
      }
      assessmentRoute {
        name
      }
      organisation {
        organisationType
        name
        parent {
          name
        }
      }
      tags {
        id
        name
      }
    }
  }
`;


export const TAG_SUGGESTIONS = gql`
  query TagSuggestions {
    tagSuggestions {
      id
      name
    }
  }
`

export const ADD_TAG_TO_ASSESSMENT = gql`
  mutation AddTagToAssessment($input: AddTagToAssessmentInput!) {
    addTagToAssessment(input: $input) {
      id
      name
    }
}`

export const REMOVE_TAG_FROM_ASSESSMENT = gql`
  mutation RemoveTagFromAssessment($input: RemoveTagFromAssessmentInput!) {
    removeTagFromAssessment(input: $input) {
      tags {
        id
        name
      }
      id
    }
}`

export const ADD_TAG = gql`
  mutation AddTag($name: String) {
    addTag(name: $name) {
      id
      name
    }
  }`

export const ADD_TAG_TO_ASSESSMENT_INVITE = gql`
  mutation AddTagToAssessmentInvite($input: AddTagToAssessmentInviteInput!) {
    addTagToAssessmentInvite(input: $input) {
      id
      name
    }
  }`

export const REMOVE_TAG_FROM_ASSESSMENT_INVITE = gql`
  mutation RemoveTagFromAssessmentInvite($input: RemoveTagFromAssessmentInviteInput!) {
    removeTagFromAssessmentInvite(input: $input) {
      tags {
        id
        name
      }
      id
    }
  }`

export const ASSESSMENTS_ROUTES = gql`
  query AssessmentRoutes {
    myAssessmentRoutes {
      id
      name
      url
      description
    }
  }
`;

export const RESOURCES = gql`
  query Resources {
    resources {
      id
      description
      link
      image
      title
    }
  }
`;



export const SEND_ASSESSMENT_INVITE = gql`
  mutation SendInvite(
    $name: String!
    $emailAddress: String!
    $venture: String!
    $language: String!
    $message: String!
    $assessmentRoute: Int,
    $tags: [TagInput!]
  ) {
    sendInvite(name: $name, emailAddress: $emailAddress, venture: $venture, language: $language, message: $message, assessmentRoute: $assessmentRoute, tags: $tags) {
      code
    }
  }
`;

export const SHARE_REVIEW = gql`
  mutation ShareReview(
    $name: String!
    $emailAddress: String!
    $message: String!
  ) {
    shareReview(name: $name, emailAddress: $emailAddress, message: $message) {
      code
    }
  }
`

export const SEND_ASSESSMENT_UPDATE = gql`
  mutation SendUpdateInvite(
    $name: String!
    $emailAddress: String!
    $venture: String!
    $language: String!
    $message: String!
    $assessmentRoute: Int,
    $assessmentId: Int,
      $tags: [TagInput!]
  ) {
    sendUpdateInvite(
      name: $name, 
      emailAddress: $emailAddress, 
      venture: $venture, 
      language: $language, 
      message: $message, 
      assessmentRoute: $assessmentRoute,
      assessmentId: $assessmentId,
        tags: $tags
    ) {
      code
    }
  }
`;

export const SEND_ASSESSMENT_INVITE_REMINDER = gql`
  mutation SendReminderInvite(
    $message: String!
    $assessmentInviteId: Int
  ) {
    sendInviteReminder(assessmentInviteId: $assessmentInviteId, message: $message) {
      code
    }
  }
`;



export const SHARE_ASSESSMENT = gql`
  mutation ShareAssessment(
    $name: String
    $emailAddress: String
    $scanType: String!
    $message: String!
    $assessmentId: Int,
    $language: String
  ) {
    shareAssessment(
      name: $name,
      emailAddress: $emailAddress,
      scanType: $scanType,
      message: $message,
      assessmentId: $assessmentId,
      language: $language
    ) {
      code
    }
  }
`;

export const ORGANISATIONS = gql`
  query Organisations {
    organisations {
      id
      organisationType
      name
      sector
      institutionUuid
      logoUrl
      foundingDate
      contact {
        name
        function
      }
      contactPerson
      website
      parent {
        name
      }
      createdAt
    }
  }
`

export const ORGANISATIONS_LIST = gql`
  query OrganisationList($type: String) {
    organisations(type: $type) {
      id
      name
    }
  }
`


export const CREATE_ORGANISATION = gql`
  mutation AddAdminOrganisation($input: AdminOrganisationInput) {
    addAdminOrganisation(input: $input) {
      code
      id
      userPassword
    }
  }
`

export const UPDATE_ORGANISATION = gql`
    mutation UpdateOrganisation($input: UpdateOrganisationInput) {
        updateOrganisation(input: $input) {
            code
            id
        }
    }
`

export const CREATE_USER = gql`
  mutation CreateUser($input: CreateUserInput) {
    addUserToOrganisation(input: $input) {
      code
      id
      userPassword
    }
  }
`


export const ASSESSMENT_TEST_OPTIONS = gql`
  query GetAssessmentTestOptions($environment: String!) {
    getAssessmentTestOptions(environment: $environment) {
      generator
      list
    }
  }
`

export const ORGANISATION = gql`
  query Organisation($organisationId: Int!) {
    organisation(id: $organisationId) {
      address
      city
      contactPerson
      institutionUuid
      name
      sector
      logoUrl
      parent {
        id
      }
      features {
          id
          name
          status
      }
      website
    }
  }
`
export const USERS_OF_ORGANISATION = gql`
query UsersByOrganisation($usersByOrganisationOrganisationId: Int!) {
  usersByOrganisation(organisationId: $usersByOrganisationOrganisationId) {
    emailAddress
    function
    id
    locale
    name
    organisation {
      name
      organisationType
    }
    phoneNumber
  }
}
`

export const DEACTIVATE_USER = gql`
  mutation DeactivateUser($deactivateUserId: Int!) {
    deactivateUser(id: $deactivateUserId) {
      code
    }
  }
`

export const GENERATE_SAS_TOKEN = gql`
  query GenerateSASToken {
    generateSASToken
  }
`


export const UPDATE_ASSESSMENT_OPTIONS = gql`
    mutation UpdateAssessmentOptions($id: Int!, $input: AssessmentOptionsInput) {
        updateAssessmentOptions(id: $id, input: $input) {
            id
            assessmentAskQuestionsTo
        }
    }
`

export const GET_ASSESSMENT_OPTIONS = gql`
    query AssessmentOptionsByOrgId($organisationId: Int!) {
        assessmentOptionsByOrgId(organisationId: $organisationId) {
            id
            assessmentAskQuestionsLink
            assessmentAskQuestionsTo
            assessmentColorPrimary
            assessmentCopyright
            assessmentColorSecondary
            assessmentResultToAddress
            assessmentLogo
            assessmentEmailFromName
            assessmentEmailFromAddress
            assessmentResultToName
            assessmentSignature
            assessmentTitle
            sendStartMail
            startMail
        }
    }
`
export const ASSESSMENT_RESULTS = gql`
  query ResultDataframe($assessmentId: [Int], $sector:String,$tagId: Int, $actualStage: Int) {
    resultDataframe(assessmentId: $assessmentId, sector:$sector, tagId:$tagId, actualStage: $actualStage) {
      companyCountSectors(assessmentId: $assessmentId, sector:$sector, tagId:$tagId, actualStage: $actualStage)
      companyCountMilestones(assessmentId: $assessmentId, sector:$sector, tagId:$tagId, actualStage: $actualStage)
      portfolioDataMetrics(assessmentId: $assessmentId, sector:$sector, tagId:$tagId, actualStage: $actualStage)
      companyDevBox(assessmentId: $assessmentId, sector:$sector, tagId:$tagId, actualStage: $actualStage)
      companyTable(assessmentId: $assessmentId, sector:$sector, tagId:$tagId, actualStage: $actualStage)
      tagOptionsResults(assessmentId: $assessmentId, sector:$sector, tagId:$tagId, actualStage: $actualStage)
      assessmentsOptionsResults(assessmentId: $assessmentId, sector:$sector, tagId:$tagId, actualStage: $actualStage)
      scanUsageResults(assessmentId: $assessmentId, sector:$sector, tagId:$tagId, actualStage: $actualStage)
      benchmarkDataMetrics(assessmentId: $assessmentId, sector:$sector, tagId:$tagId, actualStage: $actualStage)
    }
  }
`

export const GET_ASSESSMENT_OPTIONS_BY_UUID = gql`
    query AssessmentOptionsByAssessmentUuid($uuid: String!) {
        assessmentOptionsByAssessmentUuid(uuid: $uuid) {
            id
            assessmentAskQuestionsLink
            assessmentAskQuestionsTo
            assessmentColorPrimary
            assessmentCopyright
            assessmentColorSecondary
            assessmentResultToAddress
            assessmentLogo
            assessmentEmailFromName
            assessmentEmailFromAddress
            assessmentResultToName
            assessmentSignature
            assessmentTitle
            sendStartMail
            startMail
        }
    }
`

export const GET_ASSESSMENT_OPTIONS_AND_WELCOME_MESSAGE = gql`
    query AssessmentOptionsAndWelcomeMessageByRoute($route: String!, $languageCode: String) {
    assessmentOptionsAndWelcomeMessageByRoute(route: $route, language: $languageCode) {
        options {
            id
            assessmentAskQuestionsLink
            assessmentAskQuestionsTo
            assessmentColorPrimary
            assessmentCopyright
            assessmentColorSecondary
            assessmentResultToAddress
            assessmentLogo
            assessmentEmailFromName
            assessmentEmailFromAddress
            assessmentResultToName
            assessmentSignature
            assessmentTitle
            sendStartMail
            startMail
        }
        welcomeMessage {
            welcomeDescription
            welcomeText
        }
        instituteLogo
    }
}
`

export const NEW_ASSESSMENT = gql`
  mutation NewAssessment($input: NewAssessmentInput!) {
    newAssessment(input: $input) {
      externalUuid
    }
  }
`

export const ASSESSMENT_QUESTIONS = gql`
    query AssessmentQuestions($uuid: String!) {
        assessmentQuestions(uuid: $uuid) {
            key
            maxValue
            condition
            allowDecimals
            options {
                key
                text
                maxValue
                minValue
                maxLength
            }
            minValue
            totalValue
            validator
            prefill
            text
            type
            unitPostfix
            unitPrefix
            infoText
            category
        }
    }
`

export const ASSESSMENT_ANSWERS = gql`
  query AssessmentAnswers($uuid: String!, $version: Int) {
    assessmentAnswers(uuid: $uuid, version: $version) {
        answers
    }
  }
`

export const SAVE_ASSESSMENT_ANSWERS = gql`
  mutation SaveAssessmentAnswers($input: SaveAssessmentAnswersInput!) {
    saveAssessmentAnswers(input: $input) {
      code
      questionCount
      answerCount
    }
  }
`

export const VALIDATE_ASSESSMENT_ANSWERS = gql`
  mutation ValidateAssessmentAnswers($input: SaveAssessmentAnswersInput!) {
    validateAssessmentAnswers(input: $input) {
      code
      firstUnansweredQuestion
    }
  }
`

export const ORGANISATION_LIST = gql`
    query r($type: String, $withFinishedAssessment: Boolean) {
        myOrganisationList(type: $type, withFinishedAssessment: $withFinishedAssessment) {
            id
            name
        }
    }
`

export const ASSESSMENT_RENDERED_PDF = gql`
  mutation RenderAssessmentPdf($input: RenderAssessmentPdf!) {
    renderAssessmentPdf(input: $input)
  }
`

export const ASSESSMENT_VERSIONS = gql`
  query r($uuid: String!) {
    assessmentVersions(uuid: $uuid)
  }
`

export const SEND_ERROR = gql`
  mutation Error($input: ErrorInput!) {
    error(input: $input)
  }
`
